import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output, Self, } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { APIService } from '@fry/lib/api';
import { AuthService } from '@fry/lib/auth';
import { PDFService } from '@fry/lib/pdf';
import { ScriptLoaderService } from '@fry/lib/utils';
import { SecurityService } from '@fry/lib/security';

import {
  DialogComponentData,
  DialogComponentService,
  DialogComponentStateFactory
} from '@fry/components/common/dialog';

import {
    EnsureExclusiveExecution,
    TrackOperationProgress,
    TracksOperationProgress
} from '@fry/components/common';

import {
  PaymentMethod,
  PaymentMethodsStore,
  Transaction,
  TransactionProgress,
  TransactionsStore,
  TransactionState,
  TransactionVendorReference
} from '@fry/payments/lib';

import {
    PaymentGatewayVendor,
    PaymentGatewayIntegration,
    PaymentIntegrationFactory,
} from '@fry/payments/integrations';

import { TransactionComponentEvent } from './transaction.component.interfaces';
import { PaymentComponentState } from './payment.component.state';
import { RefundComponent } from './refund.component';


const MODAL_DIALOG_STATES: {[K: string]: DialogComponentStateFactory} = {
  'canceled':
    () => DialogComponentData.canceled(),

  'error':
    (content) => DialogComponentData.error($localize `Payment failed`, content),

  'progress':
    () => DialogComponentData.progress($localize `Processing`,
                                       $localize `This may take a little while depending on your connection`),

  'success':
    () => DialogComponentData.success()
};

/**
 * Payment handling component
 *
 * This is top-level component to handle the payments. You should interact with
 * API of this payment and use @Input(transaction) to configure transaction
 * to be handled by the component.
 *
 * Also you should listen to @Output(notification) through which component
 * notifies the clients about state, success & error notification.
 *
 * See above type definitions for the PaymentComponentEvent.
 *
 * Component state is managed separately in PaymentComponentState see for
 * different states PaymentComponent can be in.
 */
@Component({
    selector: 'eas-payment',
    templateUrl: './payment.component.html',
    providers: [
        DialogComponentService,
    ],
    standalone: false
})
export class PaymentComponent implements OnInit, OnDestroy, TracksOperationProgress {

    @Input() transaction!: Transaction;
    @Output() notification = new EventEmitter<TransactionComponentEvent>();

    public auditlog$?: Observable<any[]>;
    public error?: Error;
    public isOperationInProgress = false;
    public state: PaymentComponentState;
    public systemPaymentMethods: PaymentMethod[] = [];
    public transactionProgress: TransactionProgress;
    public view: string;

    private dialogRef?: MatDialogRef<RefundComponent>;
    public integration: PaymentGatewayIntegration;

    constructor(private authentication: AuthService,
                private dialog: MatDialog,
                private paymentMethodsStore: PaymentMethodsStore,
                private security: SecurityService,
                private transactionsStore: TransactionsStore,
                private pdfService: PDFService,
                @Self() private dialogComponentService: DialogComponentService,
                private apiService: APIService,
                private scriptLoaderService: ScriptLoaderService,
                @Inject(LOCALE_ID) private locale: string
                ) {
                    this.dialogComponentService.states = MODAL_DIALOG_STATES;
                }

    ngOnInit() {
        forkJoin([
            this.security.hasPermission('payments.capture'),
            this.authentication.isUserCurrent(this.transaction.doc.user),
            this.paymentMethodsStore.all(),
            this.transaction.resolveDependencies(this.paymentMethodsStore, this.transactionsStore),
        ]).subscribe(([canCapture, isOwner, paymentMethods, transaction]) => {
            this.integration
                = PaymentIntegrationFactory.integrationForVendor(transaction.vendor as PaymentGatewayVendor,
                                                                 this.scriptLoaderService,
                                                                 this.apiService,
                                                                 this.locale);

            this.systemPaymentMethods = paymentMethods;

            this.state = new PaymentComponentState(this.transaction, isOwner, canCapture);
            if (this.transaction.state === TransactionState.Failed) {
                this.error = new Error(this.transaction.outcome.details);
            }

            this.state.onStateChange = (state: PaymentComponentState) => {
                if (this.view === state.state) { return; }
                this.view = state.state;
                this.notification.emit({
                    type: 'state', payload: { 'state': state.state }
                });
            };

            this.auditlog$ = this.transactionsStore.getAuditLog(this.transaction.id);
        });
    }

    ngOnDestroy() { }

    //
    // Template helpers
    //

    public isReady(): boolean {
        return this.state !== undefined;
    }

    public hasError(): boolean {
        return this.error !== undefined;
    }

    public transactionState(transaction: Transaction): string {
        return TransactionState.description(transaction.state,
                                            transaction.isBeingRefunded,
                                            transaction.isRefunded);
    }

    //
    // Actions
    //

    @EnsureExclusiveExecution()
    public onAttempt() {
        this.isOperationInProgress = true;
        this.error = undefined;
        this.transactionsStore.save(this.transaction).pipe(
            switchMap(transaction => this.transactionsStore.getWithDependencies(transaction.id))
        ).subscribe(
            (transaction) => {
                this.transaction = transaction;
                this.state.next(this.transaction);
                this.isOperationInProgress = false;
            },
            (error) => {
                this.notification.emit({
                    type: 'error', payload: { 'error': error }
                });
                this.isOperationInProgress = false;
            }
        );
    }

    @EnsureExclusiveExecution()
    public onAction() {
        this.error = undefined;

        this.actionTransaction$(this.transactionsStore, this.transaction)
            .subscribe(
                transaction => {
                    this.state.next(transaction);
                    this.transaction = transaction;
                },
                error => { // Propagate HTTP API errors...
                    this.notification.emit({
                        type: 'error', payload: { 'error': error }
                    });
                }
            );
    }

    @EnsureExclusiveExecution()
    public onConfirm() {
        this.error = undefined;
        this.confirmTransaction$(this.transactionsStore, this.transaction)
            .subscribe(
                result => {
                    const transaction = result[1] as Transaction;
                    const progress = result[0] as TransactionProgress;
                    this.transaction = transaction;
                    this.state.next(transaction);
                    const event: TransactionComponentEvent = {
                        type: 'capture_success',
                        payload: { 'transaction': transaction },
                        progress
                    };
                    this.notification.emit(event);
                },
                error => { // Propagate HTTP API errors...
                    this.notification.emit({
                        type: 'error', payload: { 'error': error }
                    });
                }
        );
    }

    @EnsureExclusiveExecution()
    public onCapture(reference: TransactionVendorReference) {
        this.error = undefined;

        this.dialogComponentService.updateState(
            this.dialogComponentService.stateFor('progress')
        );

        this.captureTransaction$(this.transactionsStore,
                                 this.transaction,
                                 reference)
            .subscribe(
                result => {
                    this.handleResult(result);
                },
                error => {
                    this.dialogComponentService.updateState(
                        this.dialogComponentService.states['error'](error.message)
                    );

                    this.onError(new Error(error));
                }
        );
    }

    @TrackOperationProgress()
    onRefund(refund: Transaction) {
        this.dialogRef = this.dialog.open(RefundComponent, {
                            data: { transaction: refund }
                         });
        this.dialogRef.afterClosed().subscribe(result => {
            this.dialogRef = undefined;

            if (!result) { return; }
            this.notification.emit(result);
        });
    }

    @TrackOperationProgress()
    onCancel() {
        this.error = undefined;
        this.state.previous(this.transaction);
    }

    @EnsureExclusiveExecution()
    public onResult(result: any) {
        this.handleResult(result);
    }

    private handleResult(result: any) {
        const transaction = result[1] as Transaction;
        const progress = result[0] as TransactionProgress;

        this.transaction = transaction;
        this.transactionProgress = progress;
        this.state.next(transaction, progress);

        switch (transaction.state) {
            case TransactionState.Completed: {
                this.dialogComponentService.updateState(
                    this.dialogComponentService.stateFor('success')
                );

                const event: TransactionComponentEvent = {
                    type: 'capture_success',
                    payload: { 'transaction': transaction },
                    progress,
                };
                this.notification.emit(event);
                break;
            }
            case TransactionState.Failed: {
                const error: Error = new Error(progress.vendor['error']);

                this.dialogComponentService.updateState(
                    this.dialogComponentService.states['error'](error.message)
                );

                const event: TransactionComponentEvent = {
                    type: 'capture_error',
                    payload: { 'transaction': transaction }
                };
                this.onError(error, event);
                break;

            }
            case TransactionState.Actioned:
            case TransactionState.Pending:
                this.dialogComponentService.updateState(
                    this.dialogComponentService.stateFor('success')
                );

                // These cases are handled using this.state.next()
                break;
        }
    }

    //
    // Observables
    //

    @TrackOperationProgress()
    private captureTransaction$(store: TransactionsStore,
                                transaction: Transaction,
                                reference: TransactionVendorReference) {
        return store.capture(transaction, reference)
                    .pipe(
                        switchMap(progress => {
                            return store.getWithDependencies(transaction.id)
                                        .pipe(map(trans => [progress, trans]));
                        })
                    );
    }

    @TrackOperationProgress()
    private confirmTransaction$(store: TransactionsStore, transaction: Transaction) {
        return store.confirm(transaction).pipe(
            switchMap(progress => store.getWithDependencies(transaction.id)
                                       .pipe(map(trans => [progress, trans])))
        );
    }

    @TrackOperationProgress()
    private actionTransaction$(store: TransactionsStore, transaction: Transaction) {
        return store.action(transaction).pipe(
            switchMap(result => store.getWithDependencies(result.id))
        );
    }

    onDownload() {
        this.transactionsStore.receipt(this.transaction).pipe(
            switchMap(data => {
                return this.pdfService.htmlToPdf({
                    html: data.html
                });
            })
        ).subscribe(
            data => {
                this.pdfService.saveBlob(data, 'receipt.pdf');
            },
            err => {
                // FIXME
                console.log(err);
            }
        );
    }

    //
    // Helpers
    //

    onError(error: Error, event?: TransactionComponentEvent) {
        if (error === undefined) {
            this.error = undefined;
            return;
        }

        if (this.transaction.state === TransactionState.Pending &&
            this.transactionProgress !== undefined) {
            this.state.previous(this.transaction);
        }

        this.error = error;
        const e: TransactionComponentEvent = event !== undefined
                                             ? event
                                             : { type: 'error', payload: { 'error': error } };
        this.notification.emit(e);
    }
}
